// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  SwapVertRounded,
} from '@/constants/hardfin-standard-icons';
import { SortDirection, invert, sortDirectionSchema } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Container,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Navigate,
  Outlet,
  createFileRoute,
  useLocation,
} from '@tanstack/react-router';
import { Suspense, useRef } from 'react';
import { z } from 'zod';

const sortPropertySchema = z.enum(['name', 'count']);

type SortProperty = z.infer<typeof sortPropertySchema>;

const SORT_PROPERTY_LABELS: Record<SortProperty, string> = {
  name: 'name',
  count: 'count',
};

const searchSchema = z.object({
  sort: sortPropertySchema.optional().catch('count'),
  direction: sortDirectionSchema.optional().catch('desc'),
});

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardRoute,
  validateSearch: searchSchema,
});

function DashboardRoute() {
  const sortMenu = useDisclosure();
  const sortButtonRef = useRef<HTMLButtonElement>(null);
  const searchParameters = Route.useSearch();
  const navigate = Route.useNavigate();
  const location = useLocation();

  const sort = {
    property: searchParameters.sort ?? 'count',
    direction: searchParameters.direction ?? 'desc',
  };

  const setSort = (property: SortProperty, direction: SortDirection) => {
    navigate({ search: { ...searchParameters, direction, sort: property } });
  };

  if (location.pathname === '/dashboard') {
    return <Navigate to="/dashboard/asset" />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        paddingX={{ xs: 0, sm: 3 }}
      >
        {location.pathname !== '/dashboard/asset' && (
          <IconButton
            ref={sortButtonRef}
            onClick={sortMenu.open}
            sx={{ paddingY: 2 }}
          >
            <SwapVertRounded />
          </IconButton>
        )}
        <Menu
          anchorEl={sortButtonRef.current}
          open={sortMenu.isOpen}
          onClose={sortMenu.close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {sortPropertySchema.options.map((sortProperty) => {
            const isInset = sort.property !== sortProperty;

            const onClick = () => {
              if (sort.property === sortProperty) {
                return setSort(sortProperty, invert(sort.direction));
              }

              return setSort(sortProperty, 'desc');
            };

            return (
              <MenuItem key={sortProperty} onClick={onClick}>
                {sort.property === sortProperty && (
                  <ListItemIcon>
                    {sort.direction === 'asc' ? (
                      <ArrowUpwardRounded fontSize="small" />
                    ) : (
                      <ArrowDownwardRounded fontSize="small" />
                    )}
                  </ListItemIcon>
                )}
                <ListItemText inset={isInset}>
                  Sort by {SORT_PROPERTY_LABELS[sortProperty]}
                </ListItemText>
              </MenuItem>
            );
          })}
        </Menu>
      </Stack>
      <Box
        flex={1}
        paddingX={{ xs: 0, sm: 3 }}
        paddingBottom={3}
        display="flex"
        minWidth={0}
      >
        <Suspense>
          <Outlet />
        </Suspense>
      </Box>
    </Container>
  );
}
